import { createTheme, alpha } from '@mui/material';

// geoROAD.ai Design Tokens - Dark Teal & Emerald Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#10b981', // Emerald green
      light: '#34d399',
      dark: '#059669',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#22d3ee', // Cyan accent
      light: '#67e8f9',
      dark: '#06b6d4',
      contrastText: '#072929',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    orange: {
      main: '#f97316',
      light: '#fb923c',
      dark: '#ea580c',
    },
    yellow: {
      main: '#eab308',
      light: '#facc15',
      dark: '#ca8a04',
    },
    darkBlue: {
      main: '#1a1a2e',
      light: '#16213e',
      dark: '#0f0f1a',
    },
    background: {
      default: '#f8fafa', // Light with subtle teal tint
      paper: '#ffffff',
      dark: '#072929', // Dark forest teal
    },
    text: {
      primary: '#0f172a', // Slate 900
      secondary: '#475569', // Slate 600
    },
    divider: 'rgba(0, 0, 0, 0.06)',
    // Custom colors for easy access
    teal: {
      main: '#072929',
      light: '#0a3d3d',
      dark: '#051e1e',
    },
    emerald: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
      lineHeight: 1.6,
    },
    subtitle2: {
      fontWeight: 500,
    },
    body1: {
      lineHeight: 1.7,
    },
    body2: {
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(7, 41, 41, 0.04)',
    '0 1px 3px rgba(7, 41, 41, 0.08)',
    '0 2px 4px rgba(7, 41, 41, 0.08)',
    '0 4px 6px rgba(7, 41, 41, 0.08)',
    '0 6px 8px rgba(7, 41, 41, 0.1)',
    '0 8px 12px rgba(7, 41, 41, 0.1)',
    '0 10px 16px rgba(7, 41, 41, 0.1)',
    '0 12px 20px rgba(7, 41, 41, 0.12)',
    '0 16px 24px rgba(7, 41, 41, 0.12)',
    '0 20px 32px rgba(7, 41, 41, 0.14)',
    '0 24px 40px rgba(7, 41, 41, 0.14)',
    '0 28px 48px rgba(7, 41, 41, 0.16)',
    '0 32px 56px rgba(7, 41, 41, 0.16)',
    '0 36px 64px rgba(7, 41, 41, 0.18)',
    '0 40px 72px rgba(7, 41, 41, 0.18)',
    '0 44px 80px rgba(7, 41, 41, 0.2)',
    '0 48px 88px rgba(7, 41, 41, 0.2)',
    '0 52px 96px rgba(7, 41, 41, 0.22)',
    '0 56px 104px rgba(7, 41, 41, 0.22)',
    '0 60px 112px rgba(7, 41, 41, 0.24)',
    '0 64px 120px rgba(7, 41, 41, 0.24)',
    '0 68px 128px rgba(7, 41, 41, 0.26)',
    '0 72px 136px rgba(7, 41, 41, 0.26)',
    '0 76px 144px rgba(7, 41, 41, 0.28)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.95rem',
        },
        contained: {
          boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '1rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(7, 41, 41, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '@media (min-width: 600px)': {
            paddingLeft: 32,
            paddingRight: 32,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: alpha('#10b981', 0.1),
          color: '#059669',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#10b981',
          '&:hover': {
            color: '#059669',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
  },
});

export default theme;
